import { type PointerEvent, useCallback, useState } from 'react';

/**
 * One open menu at a time across the filter row. Radix opens a dropdown on
 * pointer-down and does not close a sibling non-modal menu first, so Filter
 * and Order by could sit open on top of each other. This hook owns the
 * "which menu is open" state and hands each trigger a pointer-down guard:
 * while another menu is open, the first press only closes it and is
 * swallowed, so the next press opens the pressed menu with nothing under it.
 */
export function useExclusiveToolbarMenus<Name extends string>() {
    const [openMenu, setOpenMenu] = useState<Name | null>(null);

    const isOpen = useCallback((name: Name) => openMenu === name, [openMenu]);

    const onOpenChange = useCallback(
        (name: Name) => (open: boolean) => {
            setOpenMenu((current) => {
                if (open) {
                    return name;
                }
                return current === name ? null : current;
            });
        },
        [],
    );

    const guard = useCallback(
        (name: Name) => (event: PointerEvent<HTMLElement>) => {
            if (openMenu === null || openMenu === name) {
                return;
            }
            setOpenMenu(null);
            event.preventDefault();
            event.stopPropagation();
        },
        [openMenu],
    );

    return { openMenu, isOpen, onOpenChange, guard };
}
